/**
 * Notification Management Page
 * 
 * Admin dashboard for multi-channel notification platform.
 * View, search, and manage EMAIL and SMS notifications.
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  TextInput,
  Select,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast
} from '@etherisc/ui-kit';
import { 
  BellIcon, 
  SearchIcon, 
  RefreshCwIcon,
  EyeIcon,
  MailIcon,
  MessageSquareIcon,
} from 'lucide-react';
import { NotificationDetailDrawer } from '../../components/notifications/NotificationDetailDrawer';
import { NotificationMetrics } from '../../components/notifications/NotificationMetrics';

interface NotificationRecipient {
  email?: string;
  phoneNumber?: string;
  name?: string;
}

interface Notification {
  id: string;
  channel: 'EMAIL' | 'SMS' | 'TELEGRAM';
  to: NotificationRecipient[];
  templateName: string;
  status: 'QUEUED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'BOUNCED' | 'FAILED' | 'DLQ';
  sentAt?: string;
  createdAt: string;
  channelMetadata: Record<string, unknown>;
}

export function NotificationManagement() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedForRetry, setSelectedForRetry] = useState<Set<string>>(new Set());
  const [showDLQOnly, setShowDLQOnly] = useState(false);

  // Load notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (selectedChannel !== 'all') params.set('channel', selectedChannel);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchTerm) params.set('recipient', searchTerm);
      
      const response = await fetch(`/api/notifications/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load notifications');
      }

      const data = await response.json();
      setNotifications(data.data.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [selectedChannel, statusFilter]);

  const handleSearch = () => {
    loadNotifications();
  };

  const handleViewDetails = (notificationId: string) => {
    setSelectedNotificationId(notificationId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedNotificationId(null);
  };

  const handleRetry = () => {
    loadNotifications(); // Refresh list after retry
  };

  const toggleSelectForRetry = (id: string) => {
    const newSet = new Set(selectedForRetry);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedForRetry(newSet);
  };

  const handleBulkRetry = async () => {
    if (selectedForRetry.size === 0) {
      toast.error('No notifications selected');
      return;
    }

    if (!confirm(`Retry ${selectedForRetry.size} notification(s)?`)) {
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const id of selectedForRetry) {
        try {
          const response = await fetch(`/api/notifications/retry/${id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }

      toast.success(`Retried ${successCount} notification(s)`, {
        description: failCount > 0 ? `${failCount} failed` : undefined
      });

      setSelectedForRetry(new Set());
      loadNotifications();
    } catch {
      toast.error('Bulk retry failed');
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    switch (status) {
      case 'DELIVERED': return 'success';
      case 'SENT': return 'info';
      case 'QUEUED': return 'neutral';
      case 'SENDING': return 'warning';
      case 'BOUNCED':
      case 'FAILED':
      case 'DLQ': return 'error';
      default: return 'neutral';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'EMAIL': return <MailIcon size={14} />;
      case 'SMS': return <MessageSquareIcon size={14} />;
      default: return <BellIcon size={14} />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecipient = (notification: Notification): string => {
    if (!Array.isArray(notification.to) || notification.to.length === 0) {
      return 'Unknown';
    }

      const first = notification.to[0];
    
    if (notification.channel === 'EMAIL') {
      const email = first.email || 'unknown';
      const display = first.name ? `${email} (${first.name})` : email;
      return notification.to.length > 1 
        ? `${display} (+${notification.to.length - 1} more)`
        : display;
    }
    
    if (notification.channel === 'SMS') {
      const phone = first.phoneNumber || 'unknown';
      const display = first.name ? `${phone} (${first.name})` : phone;
      return notification.to.length > 1 
        ? `${display} (+${notification.to.length - 1} more)`
        : display;
    }
    
    return 'Unknown';
  };

  const getSubject = (notification: Notification): string => {
    if (notification.channel === 'EMAIL' && notification.channelMetadata) {
      const metadata = notification.channelMetadata as { subject?: string };
      return metadata.subject || 'No subject';
    }
    return notification.templateName;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">
            Multi-channel notification management and audit trail
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedForRetry.size > 0 && (
            <Button onClick={handleBulkRetry} variant="default">
              <RefreshCwIcon size={16} className="mr-2" />
              Retry Selected ({selectedForRetry.size})
            </Button>
          )}
          <Button onClick={loadNotifications} variant="outline">
            <RefreshCwIcon size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs by Channel */}
      <Tabs value={selectedChannel} onValueChange={setSelectedChannel}>
        <TabsList>
          <TabsTrigger value="all">All Channels</TabsTrigger>
          <TabsTrigger value="EMAIL">
            <MailIcon size={14} className="mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="SMS">
            <MessageSquareIcon size={14} className="mr-2" />
            SMS
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedChannel} className="mt-6">
          {/* Metrics Dashboard */}
          <NotificationMetrics />
          
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <TextInput
                  id="search"
                  label="Search Recipient"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Email, phone, or name..."
                />
                <Select
                  id="status"
                  label="Status"
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value)}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'QUEUED', label: 'Queued' },
                    { value: 'SENDING', label: 'Sending' },
                    { value: 'SENT', label: 'Sent' },
                    { value: 'DELIVERED', label: 'Delivered' },
                    { value: 'BOUNCED', label: 'Bounced' },
                    { value: 'FAILED', label: 'Failed' },
                    { value: 'DLQ', label: 'DLQ' },
                  ]}
                />
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={showDLQOnly}
                      onChange={(e) => {
                        setShowDLQOnly(e.target.checked);
                        setStatusFilter(e.target.checked ? 'DLQ' : 'all');
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    DLQ Only
                  </label>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSearch} className="w-full">
                    <SearchIcon size={16} className="mr-2" />
                    Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notifications Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Notifications ({notifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8">
                  <BellIcon size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No notifications found</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try adjusting your filters or search terms
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {statusFilter === 'DLQ' && <TableHead className="w-12"></TableHead>}
                      <TableHead>Channel</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Subject / Message</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        {statusFilter === 'DLQ' && (
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedForRetry.has(notification.id)}
                              onChange={() => toggleSelectForRetry(notification.id)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            {getChannelIcon(notification.channel)}
                            {notification.channel}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {getRecipient(notification)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {getSubject(notification)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {notification.templateName}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(notification.status)}>
                            {notification.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {notification.sentAt 
                            ? formatDate(notification.sentAt)
                            : formatDate(notification.createdAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(notification.id)}
                          >
                            <EyeIcon size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Drawer */}
      <NotificationDetailDrawer
        notificationId={selectedNotificationId}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onRetry={handleRetry}
      />
    </div>
  );
}

